import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useSession } from "../../auth/session"
import { useSelector } from "react-redux"
import { redeemCodeState } from "../RedeemCodeCard"
import { RootState } from "../../redux/store"
import { License, LicenseType } from "../../api/models"
import Spinner from "../widgets/Spinner"
import { useLicenseCollection } from "../../redux/licensesSlice"
import { useLibrary } from "../../redux/librarySlice"
import { FetchableStateStatus } from "../../redux/fetchableState"
import { installerSkus, InstallerSku, primaryDownload } from "./Installers"
import { platformLabels } from "../widgets/platform"
import { Icon } from "../widgets/icons"
import { useOffers } from "../../redux/storeSlice"
import { meSelector } from "../../redux/globalSlice"
import { useSubscription } from "../../redux/subscriptionSlice"
import { useActivePlans, useExtraTrialDays } from "../../redux/plansSlice"
import { useSku } from "../../redux/skusSlice"
import { useProduct } from "../../redux/productsSlice"
import PointsBalancePill from "../widgets/PointsBalancePill"
import { RENT_TO_OWN_ENABLED } from "../../featureFlags"
import { useOffersPageEnabled } from "../../offersAccess"
import { useIlokAccounts } from "../../hooks/useIlokAccounts"
import { LINK_ILOK_STATE } from "../IlokAccountsCard"
import { flagSet, setFlag } from "../IlokUpgradeBanner"

export default function Dashboard() {
  const navigate = useNavigate()
  const offersEnabled = useOffersPageEnabled()
  const session = useSession()
  const me = useSelector(meSelector)?.data;
  const accessToken = session.accessToken ?? ""
  const { data: subscription } = useSubscription(accessToken)
  const { collection: licenseCollection } = useLicenseCollection(accessToken)
  const entities = useSelector((state: RootState) => state.licenses.entities)
  const { data: library, status: libraryStatus } = useLibrary(accessToken)

  if (!me) {
    return (
      <div className="page">
        <h1 className="page-title">Welcome back</h1>
        <div className="empty-state">
          <Spinner size={16} /> Loading your dashboard…
        </div>
      </div>
    )
  }
  
  const licenseGroups = Object.entries(licenseCollection.byProduct).map(([productId, { ids: licenseIds }]) => {
    const licenses = licenseIds
      .map((licenseId) => entities[licenseId]?.data)
      .filter((lic): lic is License => lic != null)
    return {
      productId,
      activeCount: licenses.reduce((sum, l) => sum + l.activationIds.active.length, 0),
    }
  }).filter((g) => g.activeCount >= 0)

  const licensesLoading = licenseCollection.currentRequestId !== undefined && licenseCollection.lastFetched === undefined

  const trials = Object.values(entities)
    .map((entity) => entity?.data)
    .filter((lic): lic is License =>
      lic != null && lic.type === LicenseType.Demo && lic.expiresAt !== undefined && new Date(lic.expiresAt) > new Date())

  // undefined subscription means loading, null skuId means no subscription, otherwise show subscription details

  return (
    <div className="page">
      <div className="dashboard-header">
        <h1 className="page-title" style={{ margin: 0 }}>Welcome back, {me.firstname ?? "@" + me.id}</h1>
        <PointsBalancePill />
      </div>
      <div className="card-grid">
        {RENT_TO_OWN_ENABLED && (
          <SubscriptionCard skuId={subscription === undefined ? undefined : subscription?.skuId ?? null} />
        )}
        <LibraryCard licenseGroups={licenseGroups} loading={licensesLoading} />
        <InstallersCard
          installers={library ? installerSkus(library) : undefined}
          failed={library === undefined && libraryStatus === FetchableStateStatus.Error}
        />
        <TrialsCard trials={trials} />
      </div>

      {offersEnabled && <OffersTeaser />}

      <RedeemCard onRedeem={(code) => navigate("/redemptions", { state: code ? redeemCodeState(code) : null })} />
      <LinkIlokCard />
      {/* <LoyaltyBanner /> */}
    </div>
  )
}

// Compact Dashboard teaser for "Your Offers" — only shown when the user actually
// has personalized offers. Links through to the full /offers page.
function OffersTeaser() {
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const { loading, upgrades, crossgrades, free, rewards, total } = useOffers(accessToken)
  // Trigger the active-plans fetch so extraTrialDays is populated on the dashboard.
  useActivePlans(accessToken)
  const extraTrialDays = useExtraTrialDays()
  const extraDays = extraTrialDays.reduce((sum, e) => sum + e.days, 0)
  // the bonus-trial offer starts a rent-to-own plan, so it goes with that feature
  const hasTrialOffer = RENT_TO_OWN_ENABLED && extraDays > 0

  if (loading) return null

  const offerCount = total + (hasTrialOffer ? 1 : 0)
  if (offerCount === 0) return null

  const parts: string[] = []
  if (hasTrialOffer)
    parts.push(`${extraDays} bonus trial day${extraDays === 1 ? "" : "s"} on a subscription`)
  if (upgrades.length > 0)
    parts.push(`${upgrades.length} upgrade${upgrades.length === 1 ? "" : "s"}`)
  if (crossgrades.length > 0)
    parts.push(`${crossgrades.length} crossgrade deal${crossgrades.length === 1 ? "" : "s"}`)
  if (free.length > 0)
    parts.push(`${free.length} free product${free.length === 1 ? "" : "s"}`)
  if (rewards.length > 0)
    parts.push(`${rewards.length} reward${rewards.length === 1 ? "" : "s"} you can claim`)

  const detail = parts.length <= 2
    ? parts.join(" and ")
    : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`

  return (
    <Link to="/offers" className="offers-teaser">
      <div className="offers-teaser-text">
        <span className="card-title">Your Offers <span className="badge offers-teaser-badge">{offerCount}</span></span>
        <span className="offers-teaser-detail">{detail}</span>
      </div>
      <span className="card-link">View all offers →</span>
    </Link>
  )
}

function SubscriptionCard({ skuId }: { skuId?: string | null }) {
  if (skuId === undefined) {
    return (
      <ClickableCard to="/plan">
        <h2 className="card-title">Rent-to-Own</h2>
        <p className="empty-state"><Spinner size={12} /> Loading…</p>
      </ClickableCard>
    )
  }

  if (skuId === null) {
    return (
      <ClickableCard to="/plan">
        <h2 className="card-title">Rent-to-Own</h2>
        <p className="card-detail">No active rent-to-own plan</p>
        <div className="card-links">
          <Link to="/plan" className="card-link" onClick={(e) => e.stopPropagation()}>View plans</Link>
        </div>
      </ClickableCard>
    )
  }

  return (
    <ClickableCard to="/plan">
      <h2 className="card-title">Rent-to-Own</h2>
      <PlanSkuDetails skuId={skuId} />
      <div className="card-links">
        <Link to="/plan" className="card-link" onClick={(e) => e.stopPropagation()}>Manage your rent-to-own plan</Link>
      </div>
    </ClickableCard>
  )
}

function ClickableCard({ to, children }: { to: string, children: React.ReactNode }) {
  const navigate = useNavigate()
  return (
    <div
      className="card card-clickable"
      role="button"
      tabIndex={0}
      onClick={() => navigate(to)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(to) } }}
    >
      {children}
    </div>
  )
}

function PlanSkuDetails({ skuId }: { skuId: string }) {
  const { data: sku } = useSku(skuId)

  if (!sku) {
    return <p className="empty-state"><Spinner size={12} /> Loading…</p>
  }

  return (
    <>
      <p className="card-value">{sku.name}</p>
      <p className="card-detail">{sku.description}</p>
    </>
  )
}

function LibraryCard({ licenseGroups, loading }: { licenseGroups: { productId: string; activeCount: number }[]; loading: boolean }) {
  return (
    <ClickableCard to="/licenses">
      <h2 className="card-title">Licenses</h2>
      <div className="card-detail">
        {loading && <span className="empty-state"><Spinner size={12} /> Loading…</span>}
        {!loading && licenseGroups.length === 0 && <p>You have no licenses</p>}
        {licenseGroups.slice(0, 2).map(({ productId, activeCount }) => (
          <div key={productId} className="license-summary-row">
            <ProductName productId={productId} />
            <span className="badge">{activeCount} active</span>
          </div>
        ))}
        {licenseGroups.length > 2 && (
          <p className="card-detail" style={{ marginTop: 8 }}>and {licenseGroups.length - 2} more</p>
        )}
      </div>
      <div className="card-links">
        <Link to="/licenses" className="card-link" onClick={(e) => e.stopPropagation()}>View licenses</Link>
        <Link to="/store" className="card-link" onClick={(e) => e.stopPropagation()}>Get more</Link>
      </div>
    </ClickableCard>
  )
}

// The Installers page in two rows: the first skus in its order (bundles
// first), each with the one-click download for this platform — the latest
// release, or the user's test build. undefined = still loading.
function InstallersCard({ installers, failed }: { installers?: InstallerSku[], failed: boolean }) {
  return (
    <ClickableCard to="/installers">
      <h2 className="card-title">Installers</h2>
      <div className="card-detail">
        {installers === undefined && !failed && <span className="empty-state"><Spinner size={12} /> Loading…</span>}
        {installers === undefined && failed && <p>Couldn't load your installers</p>}
        {installers?.length === 0 && <p>Nothing to install yet</p>}
        {installers?.slice(0, 2).map((installer) => {
          const download = primaryDownload(installer)
          return (
            <div key={installer.sku.id} className="license-summary-row">
              <span>{installer.sku.name}</span>
              {download ? (
                <a
                  href={download.url}
                  className="card-link"
                  style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                  title={`Get v${download.version} for ${platformLabels[download.platform]}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  v{download.version} <Icon name="installer" />
                </a>
              ) : (
                <span className="badge">no installer</span>
              )}
            </div>
          )
        })}
        {installers !== undefined && installers.length > 2 && (
          <p className="card-detail" style={{ marginTop: 8 }}>and {installers.length - 2} more</p>
        )}
      </div>
      <div className="card-links">
        <Link to="/installers" className="card-link" onClick={(e) => e.stopPropagation()}>View installers</Link>
      </div>
    </ClickableCard>
  )
}

// Time-boxed product evaluations — trials aren't entitlements, so they live
// here rather than in the per-SKU Library. Renders only while one is running;
// each row links to the license detail for device management.
function TrialsCard({ trials }: { trials: License[] }) {
  if (trials.length === 0) return null
  return (
    <div className="card">
      <h2 className="card-title">Active Trials</h2>
      <div className="card-detail">
        {trials.map((license) => (
          <Link key={license.id} to={`/licenses/${license.id}`} className="license-summary-row trial-summary-row">
            <ProductName productId={license.productId} />
            {license.expiresAt && <span className="badge badge-trial">{trialDaysLeft(license.expiresAt)}</span>}
          </Link>
        ))}
      </div>
    </div>
  )
}

// Full-width banner below the Redeem card inviting users with NO iLok
// account rows yet (a pending link counts as already engaged) to link one: licenses held
// in a linked iLok account become native entitlements automatically. It
// disappears by itself once an account row exists, or for good when the user
// dismisses it ("Don't show again") — per-user in localStorage like the
// Offers reminders (a fresh browser shows it once more — acceptable).
// Following the CTA does NOT dismiss it: if they abandon the linking it
// returns. Renders nothing while the accounts are unknown (loading or
// failed) so a hiccup on our side never nags. The CTA lands on the Account
// page with the link dialog already open (router state read by
// IlokAccountsCard).
const linkIlokDismissedKey = (userId: string) => `linkIlokCardDismissed:${userId}`

function LinkIlokCard() {
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const me = useSelector(meSelector)?.data
  const navigate = useNavigate()
  const { accounts } = useIlokAccounts(accessToken)
  const [dismissed, setDismissed] = useState(() => me === undefined || flagSet(linkIlokDismissedKey(me.id)))

  if (!me || dismissed || accounts === undefined || accounts.length > 0) return null

  const goLink = () => navigate("/account", { state: LINK_ILOK_STATE })
  const dismiss = () => {
    setFlag(linkIlokDismissedKey(me.id))
    setDismissed(true)
  }

  return (
    <div
      className="card card-clickable"
      role="button"
      tabIndex={0}
      onClick={goLink}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goLink() } }}
    >
      <h2 className="card-title">Own iLok licenses?</h2>
      <p className="card-detail">Link your iLok account and get free native licenses.</p>
      <div className="card-links">
        <span className="card-link">Link iLok account</span>
        <button
          type="button"
          className="card-link"
          style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
          onClick={(e) => { e.stopPropagation(); dismiss() }}
        >
          Don't show again
        </button>
      </div>
    </div>
  )
}

function trialDaysLeft(expiresAt: string): string {
  const days = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000)
  return days <= 1 ? "expires today" : `${days} days left`
}

function RedeemCard({ onRedeem }: { onRedeem: (code?: string) => void }) {
  const [dragOver, setDragOver] = useState(false)
  return (
    <div
      className={`card card-redeem${dragOver ? " card-redeem-dragover" : ""}`}
      onClick={() => onRedeem()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onRedeem()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
      onDragEnter={(e) => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        const text = e.dataTransfer.getData("text/plain").trim()
        if (text) onRedeem(text)
      }}
    >
      <h2 className="card-title">Redeem Code</h2>
      <p className="card-detail">Have a license code? Redeem it here.</p>
      <div className="card-links" style={{ marginTop: 8 }}>
        <span className="card-link">{dragOver ? "Drop code here" : "Enter code"}</span>
        <Link to="/redemptions" className="card-link" onClick={(e) => e.stopPropagation()}>View past redemptions</Link>
      </div>
    </div>
  )
}

// function LoyaltyBanner() {
//   return (
//     <div className="loyalty-banner">
//       <span>You have <strong>{mockLoyaltyBalance.toLocaleString()}</strong> loyalty points{redeemedPointTopups.length > 0 && <span className="badge-new">NEW</span>}</span>
//       <Link to="/loyalty" className="card-link">View history</Link>
//     </div>
//   )
// }

function ProductName({ productId }: { productId: string }) {
  const session = useSession()
  const accessToken = session.accessToken || ""
  const { data: product } = useProduct(productId, accessToken)

  if (!product) {
    return (
      <div className="empty-state">
         <Spinner size={12} /> Loading…
      </div>
    )
  }
  return <span>{product.name}</span>
}
